/**
 * Cable profiles -- group 12.
 *
 * Kharma, Audio Sensibility, Cullen Cable, Grimm Audio, Ricable, XLO and
 * Monster Cable.
 *
 * None of these seven makers publishes loop resistance, inductance and
 * capacitance figures. Several publish conductor gauge, conductor material,
 * dielectric and geometry in detail -- Cullen Cable and Audio Sensibility in
 * particular state AWG and construction outright -- so those facts are recorded
 * here and the kit derives R, L and C from them. Where a maker states nothing,
 * the entry says so in its provenance note rather than inventing a number.
 *
 * Power, USB and Ethernet entries describe the build and stop there. The engine
 * applies no signal processing to those three types, and a well-made 3000 dollar
 * mains cord is still a well-made mains cord with zero DSP attached to it.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_12: CableProfile[] = [
  // =========================================================================
  // Kharma -- Netherlands. Silver and silver-gold solid core, leather sleeving,
  // and almost no published electrical data at any price.
  // =========================================================================
  makeCable({
    mfr: 'Kharma',
    model: 'Enigma Veyron Loudspeaker Cable',
    cableType: 'speaker',
    price: usd(43500, 'APPROXIMATE', 2024, 'From EUR 40,000 for a 2 m pair; converted, and rising with length.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 12,
    dielectric: 'Air; each conductor runs in its own chamber',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b08d57',
    tags: ['flagship', 'air-dielectric', 'leather-sleeved'],
    provenanceNote:
      'Kharma states that every conductor is individually chambered for air insulation and that the spades and plugs are cast pure silver. It does not publish conductor alloy, gauge or any electrical figure, so the 12 AWG here is an assumption for a cable of this diameter and the R, L and C are derived from gauge, material and geometry.',
    notes:
      'Each conductor sits in its own air chamber rather than in a solid extruded jacket. Wide spacing in a low permittivity dielectric is the high-inductance, low-capacitance end of the trade-off: series inductance is what a speaker cable pushes against, so the geometry that helps a long interconnect is the one that costs you at the loudspeaker end. The engine works the divider out from the derived figures and the load impedance.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Enigma Veyron Interconnect Cable',
    cableType: 'interconnect',
    price: usd(24500, 'APPROXIMATE', 2023, 'EUR 22,500 for a 1 m pair on the 2023 retail list; converted.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'solid-core',
    gauge: 22,
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b08d57',
    tags: ['flagship', 'solid-conductor'],
    provenanceNote:
      'Kharma describes solid core silver, gold and carbon conductors, an XLR with silver plated pins and an RCA with a pure silver centre pin. Gauge is not published; 22 AWG is assumed from the cable diameter. No electrical figures are published, so all three are derived.',
    notes:
      'Solid core silver-gold conductors with a carbon layer, terminated in RCA and XLR plugs of Kharma design. Solid, widely spaced conductors give low capacitance, which is the property that matters for a line-level link: at these values a 1 m run works out to tens of picofarads and the low-pass corner it forms with any normal source impedance sits far above the audio band. The engine computes it and reports what it comes to.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Enigma Veyron Power Cable',
    cableType: 'power',
    price: usd(18500, 'APPROXIMATE', 2024, 'From EUR 17,000 for a 2 m cord (KPC-EV-1.0); converted.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Dual layer screen, plus vibration damping tubing',
    termination: ['IEC C15', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b08d57',
    tags: ['flagship', 'shielded', 'high-current'],
    provenanceNote:
      'Construction as described by Kharma: pure silver conductors combined with carbon, dual layer screening, damping tubing, shielded plugs in EU or US, standard or high current IEC. Gauge and current rating are not published and are assumed from the connector options.',
    notes:
      'Pure silver conductors with a carbon layer that Kharma describes as a skin-effect measure rather than a conductor, under a two-layer screen, in an embroidered leather sleeve, with standard and high-current IEC options. That is a genuinely elaborate piece of cable assembly. The engine applies NO signal processing to power cords, so choosing this changes the chain description and the price and leaves the audio bit-identical.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Exquisite Loudspeaker Cable',
    cableType: 'speaker',
    price: usd(14500, 'APPROXIMATE', 2022, 'EUR 13,500 for a 2 m pair (KLC-EX-1.0) on the 2022 retail list.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'solid-core',
    gauge: 13,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b08d57',
    tags: ['solid-conductor'],
    provenanceNote:
      'Model code KLC-EX-1.0. Kharma documents solid core silver-gold conductors for the Exquisite interconnect and this loudspeaker cable is taken to share that conductor set; gauge is not published and 13 AWG is assumed. All electrical figures are derived.',
    notes:
      'Solid core silver-gold conductors, silver anodised end parts, sold per pair at 2 m with a per-half-metre supplement beyond that. Silver-gold has roughly ten percent higher resistivity than pure silver, so for the same gauge the loop resistance is slightly higher; over a 2 m run into an 8 ohm nominal load that difference is far below the level at which anything measurable happens.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Exquisite Interconnect Cable',
    cableType: 'interconnect',
    price: usd(8700, 'APPROXIMATE', 2023, 'EUR 8,000 for a 1 m pair (KIC-EX-1.0) on the 2023 retail list.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'solid-core',
    gauge: 22,
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b08d57',
    tags: ['solid-conductor'],
    provenanceNote:
      'Model codes KIC-EX-1.0 (analogue). Kharma publishes solid core silver-gold and carbon conductors; gauge is not published and 22 AWG is assumed. Electrical figures derived.',
    notes:
      'Solid core silver-gold with a carbon layer, offered single-ended or balanced. A solid conductor pair with generous spacing is the low-capacitance geometry, which is the only construction property of a line-level cable the engine can act on.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Exquisite Digital Interconnect Cable',
    cableType: 'interconnect',
    price: usd(3900, 'APPROXIMATE', 2022, 'EUR 3,600 for a single 1 m cable (KDC-EX-1.0) on the 2022 retail list.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'coaxial',
    gauge: 22,
    characteristicOhms: 75,
    digitalStandard: 'S/PDIF, 75 ohm coaxial',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#b08d57',
    tags: ['digital', 'impedance-controlled'],
    provenanceNote:
      'Model code KDC-EX-1.0. Kharma lists a digital cable in the Exquisite line and describes the same conductor set as the analogue version; the 75 ohm characteristic impedance is the S/PDIF requirement rather than a published Kharma figure. Gauge assumed.',
    notes:
      'A 75 ohm coaxial lead built from the Exquisite conductor set. It is filed as an interconnect because that is the nearest category in this schema, but what it carries is a self-clocked S/PDIF stream, not a line-level analogue signal, so the low-pass calculation the engine performs on interconnects is not a meaningful description of what this cable does in a chain.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Exquisite Power Cable',
    cableType: 'power',
    price: usd(5500, 'APPROXIMATE', 2023, 'EUR 5,100 for a 2 m cord (KPC-EX-1.1) on the 2023 retail list.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Shielded, with shielded plugs',
    termination: ['IEC C15', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#b08d57',
    tags: ['shielded'],
    provenanceNote:
      'Kharma publishes the connector options and silver plated plugs for KPC-EX-1.1 but not the conductor alloy or gauge. Silver plated copper is assumed by analogy with the Elegance cord, which Kharma does describe as silvered copper with carbon; gauge assumed at 12 AWG.',
    notes:
      'A shielded mains cord with silver plated plugs, offered in EU and US form with standard or high-current IEC. Everything above is cable assembly, and it is applied to the segment of the chain that sits ahead of a rectifier and reservoir bank. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Elegance Loudspeaker Cable',
    cableType: 'speaker',
    price: usd(2900, 'APPROXIMATE', 2022, 'EUR 2,700 for a 2 m pair (KLC-EL-1.0) on the 2022 retail list.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'solid-core',
    gauge: 14,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b08d57',
    tags: ['solid-conductor'],
    provenanceNote:
      'Model code KLC-EL-1.0, priced per 2 m pair with a EUR 650 supplement per extra half metre. Kharma documents solid core silver-gold for the Elegance interconnect; the loudspeaker cable is assumed to share it. Gauge not published, assumed 14 AWG. Electrical figures derived.',
    notes:
      'The entry point into the current Kharma cable range, in black anodised aluminium hardware to match the Elegance loudspeakers. At 14 AWG the loop resistance over a 2 m run is a few tens of milliohms, which against a 4 ohm minimum is a fraction of a decibel of level and a small change in the damping the amplifier exerts. That is the whole of the modelled effect.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Elegance Interconnect Cable',
    cableType: 'interconnect',
    price: usd(1450, 'APPROXIMATE', 2022, 'EUR 1,350 for a 1 m pair (KIC-EL-1.0) on the 2022 retail list.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'solid-core',
    gauge: 22,
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b08d57',
    tags: ['solid-conductor'],
    provenanceNote:
      'Model codes KIC-EL-1.0 and KDC-EL-1.0. Kharma states solid core silver-gold conductors. Gauge is not published and is assumed at 22 AWG; electrical figures derived.',
    notes:
      'Solid core silver-gold conductors in the Elegance hardware, single-ended or balanced. Nothing in the construction changes the fact that a metre of low-capacitance interconnect between a normal source and a normal input is electrically almost inert in the audio band, and the engine will report that.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Elegance Power Cable',
    cableType: 'power',
    price: usd(760, 'APPROXIMATE', 2022, 'EUR 700 for a 2 m cord (KPC-EL-1.0) on the 2022 retail list.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    termination: ['IEC C15', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b08d57',
    tags: ['hybrid-conductor'],
    provenanceNote:
      'Model code KPC-EL-1.0. Kharma describes a hybrid of silvered copper conductors and carbon. Gauge and current rating are not published and are assumed.',
    notes:
      'A hybrid mains cord: silvered copper conductors with a carbon layer, in the black anodised Elegance hardware. The engine applies no signal processing to power cords, so this entry exists to describe the cord and its price accurately, not to claim an audio effect.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Matrix Reference Loudspeaker Cable',
    cableType: 'speaker',
    price: usd(1600, 'APPROXIMATE', 2022, 'Not on the current Kharma retail sheet; approximate street price for a 2 m pair.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'solid-core',
    gauge: 14,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b08d57',
    tags: ['value-line'],
    provenanceNote:
      'Model code KLC-MR-1a. Kharma describes pure silver and silver-gold conductors for the Matrix Reference loudspeaker cable but no gauge or electrical data, and the line no longer appears on the current retail price list, so the price is an approximation rather than a quoted figure.',
    notes:
      'The value line, using the same silver and silver-gold conductor stock as the dearer Kharma cables in a simpler dress. Gauge and run length set the loop resistance, and that is the number that decides whether a speaker cable does anything measurable at all.',
  }),

  makeCable({
    mfr: 'Kharma',
    model: 'Matrix Reference Power Cable',
    cableType: 'power',
    price: usd(700, 'APPROXIMATE', 2022, 'Not on the current Kharma retail sheet; approximate street price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 12,
    currentRatingA: 16,
    termination: ['IEC C15', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b08d57',
    tags: ['crossed-quad', 'value-line'],
    provenanceNote:
      'Model code KPC-MR-1a. Kharma describes heavy gauge stranded copper conductors in a crossed quad formation with gold plated plugs. Gauge, current rating and price are not published and are assumed or approximated.',
    notes:
      'Heavy stranded copper in a crossed quad lay, which is the same field-cancelling arrangement used in star-quad microphone cable and does reduce the loop area a mains cord presents to and radiates into its surroundings. It is a real construction choice with a real electromagnetic rationale. It is also upstream of a rectifier, and the engine applies no signal processing to power cords.',
  }),

  // =========================================================================
  // Audio Sensibility -- Canada. OCC copper and OCC silver, cryogenically
  // treated, hand assembled, and unusually willing to state AWG and geometry.
  // =========================================================================
  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Impact SE Power Cable',
    cableType: 'power',
    price: usd(120, 'APPROXIMATE', 2024, 'About USD 120 for a 1 m cord; the maker prices in Canadian dollars.'),
    priceLengthM: 1,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['cryo-treated', 'entry-line'],
    provenanceNote:
      'Audio Sensibility states OCC copper conductors and cryogenic treatment of cable and connectors across the range but does not publish the gauge of the Impact SE cord; 12 AWG is assumed as typical for a 15 A mains lead of this diameter.',
    notes:
      'The entry mains cord: cryogenically treated OCC copper, hand assembled in Canada, sold with a ten year guarantee. Gauge and current rating are what a mains cord is actually for. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Testament Power Cable',
    cableType: 'power',
    price: usd(230, 'APPROXIMATE', 2024, 'About USD 230 for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'star-quad',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['cryo-treated'],
    provenanceNote:
      'Audio Sensibility publishes OCC copper and cryogenic treatment for the Testament line and uses a four-conductor star quad lay in the Testament speaker cable; the same lay is assumed for the cord. Gauge, current rating and price are approximations, not published figures.',
    notes:
      'Cryogenically treated OCC copper in the Testament dress, a step above the Impact SE cord in conductor count and jacket. Everything here is assembly quality and current capacity. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Signature Power Cable V2',
    cableType: 'power',
    price: usd(340, 'APPROXIMATE', 2024, 'About USD 340 for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['cryo-treated', 'shielded'],
    provenanceNote:
      'The V2 designation and the OCC copper conductor material are published by Audio Sensibility; gauge, shielding and price are not stated in the material reviewed and are approximations.',
    notes:
      'A heavier OCC copper cord than the Impact SE, with C19 available for equipment drawing serious current. As with every mains lead in this database, gauge and rating are the honest specification and the DSP contribution is exactly zero.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Statement Power Cable',
    cableType: 'power',
    price: usd(700, 'APPROXIMATE', 2024, 'About USD 700 for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'star-quad',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['cryo-treated', 'high-current'],
    provenanceNote:
      'Audio Sensibility publishes OCC conductor material and cryogenic treatment for the Statement line; the gauge, geometry, rating and price recorded here are approximations for the top copper cord and are not quoted figures.',
    notes:
      'The heaviest cord in the Audio Sensibility copper range, built for amplifiers that pull large transient current and available with 20 A terminations. Good mains cable engineering, applied where the engine models nothing at all.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Testament Single-Wire Speaker Cable',
    cableType: 'speaker',
    price: usd(279, 'APPROXIMATE', 2024, 'About USD 279 for a 2.5 m pair; priced by length and termination.'),
    priceLengthM: 2.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'star-quad',
    gauge: 14,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9fb4c7',
    tags: ['cryo-treated', 'published-construction'],
    provenanceNote:
      'Construction as published by Audio Sensibility: four cryogenically treated 14 AWG OCC copper conductors, two signal and two return, in a star quad lay with Teflon dielectric and a grey Techflex jacket. No electrical figures are published, so R, L and C are derived from that construction.',
    notes:
      'Four 14 AWG OCC copper conductors in star quad, paralleled two and two, which is the same arrangement Canare uses in 4S11 and which halves the effective loop area. Two 14 AWG conductors in parallel behave as roughly 11 AWG, so the loop resistance over a 2.5 m run lands in the low tens of milliohms. Teflon dielectric keeps capacitance modest, which for a speaker cable is irrelevant either way.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Statement Single-Wire Speaker Cable',
    cableType: 'speaker',
    price: usd(600, 'APPROXIMATE', 2024, 'About USD 600 for a 2.5 m pair in the OCC copper version.'),
    priceLengthM: 2.5,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'star-quad',
    gauge: 12,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['cryo-treated'],
    provenanceNote:
      'Audio Sensibility sells the Statement speaker cable in OCC copper and in OCC silver; the copper version is modelled here. Gauge, geometry and price are approximations by analogy with the published Testament construction, not quoted figures.',
    notes:
      'The Statement single-wire cable steps up conductor section from the Testament while keeping the four-conductor lay and Teflon dielectric. A silver version is also offered; silver lowers resistivity by about eight percent for the same section, which at these run lengths shifts the loop resistance by a couple of milliohms.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Statement SE Analog Interconnect RCA',
    cableType: 'interconnect',
    price: usd(400, 'APPROXIMATE', 2024, 'About USD 400 for a 1 m pair in OCC copper.'),
    priceLengthM: 1,
    country: 'Canada',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 20,
    dielectric: 'Teflon (PTFE)',
    shielding: 'Braided copper screen',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['cryo-treated'],
    provenanceNote:
      'The model name and the OCC copper conductor are published; gauge, dielectric, shielding and price are approximations consistent with the rest of the range rather than quoted figures.',
    notes:
      'Cryogenically treated OCC copper, single-ended, hand terminated. A shielded twisted pair of this section presents somewhere near 70 pF per metre, so a 1 m run against a typical solid-state output impedance puts the low-pass corner in the megahertz. The engine computes the in-band result, which is approximately nothing.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Statement SE Silver Analog Interconnect',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2024, 'About USD 700 for a 1 m pair in solid OCC silver.'),
    priceLengthM: 1,
    country: 'Canada',
    conductor: 'pure-silver',
    geometry: 'solid-core',
    gauge: 22,
    dielectric: 'Teflon (PTFE)',
    shielding: 'Braided screen',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['cryo-treated', 'solid-conductor'],
    provenanceNote:
      'Audio Sensibility publishes solid OCC silver conductors for the Statement silver interconnects; gauge, dielectric and price are approximations rather than quoted figures.',
    notes:
      'Solid core OCC silver in Teflon. Silver has the lowest resistivity of any conductor in common use, about eight percent below copper, which on a line-level cable driving a 10 kilohm input is a difference with no path to audibility. The solid, widely spaced geometry keeps capacitance low, and that is the property the engine acts on.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Signature Silver XLR Interconnect',
    cableType: 'interconnect',
    price: usd(450, 'APPROXIMATE', 2024, 'About USD 450 for a 1 m balanced pair.'),
    priceLengthM: 1,
    country: 'Canada',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Teflon (PTFE)',
    shielding: 'Braided screen, drain to pin 1',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['balanced', 'cryo-treated'],
    provenanceNote:
      'The Signature silver XLR interconnect is a published product and the silver conductor material is stated; gauge, shield detail and price are approximations.',
    notes:
      'A twisted silver pair inside a braided screen with the drain landed on pin 1, which is the correct way to keep shield current out of the signal return in a balanced link. Twisting sets the loop area and therefore the inductance; the screen does not carry signal.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Statement Silver AES/EBU',
    cableType: 'interconnect',
    price: usd(260, 'APPROXIMATE', 2024, 'About CAD 350 for a 1.5 m cable; converted.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 110,
    digitalStandard: 'AES3 (AES/EBU), 110 ohm',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['digital', 'balanced', 'impedance-controlled'],
    provenanceNote:
      'Audio Sensibility lists a Statement silver AES/EBU cable and the 110 ohm figure is the AES3 requirement, not a published measurement of this cable. Gauge is assumed.',
    notes:
      'A 110 ohm balanced pair for AES3, in OCC silver. Filed as an interconnect for want of a nearer category; the traffic on it is a self-clocked digital stream, so the analogue low-pass the engine computes for interconnects does not describe what this lead does in service.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Statement Silver USB',
    cableType: 'usb',
    price: usd(400, 'APPROXIMATE', 2024, 'About USD 400 for a 1.5 m assembly.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['usb-2.0', 'cryo-treated'],
    provenanceNote:
      'Audio Sensibility publishes solid OCC silver conductors for the Statement USB cable. Gauge, shield construction, the 90 ohm figure (which is the USB requirement) and the price are assumptions or approximations.',
    notes:
      'Solid OCC silver data pair, separately screened, hand terminated in Canada. USB carries packetised, error-checked data and an asynchronous DAC generates its own clock, so a compliant assembly either transfers the data or fails to. The engine applies no signal processing here.',
  }),

  makeCable({
    mfr: 'Audio Sensibility',
    model: 'Signature Ethernet',
    cableType: 'ethernet',
    price: usd(300, 'APPROXIMATE', 2024, 'About USD 300 for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'Canada',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Ethernet over RJ45; no Cat category certification published',
    shielding: 'Shielded, drain to connector shell',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#9fb4c7',
    tags: ['network', 'hand-assembled'],
    provenanceNote:
      'Audio Sensibility describes the Signature Ethernet cable as solid OCC silver. It does not publish a Cat category certification, so none is claimed here; gauge, shielding and price are approximations.',
    notes:
      'Solid OCC silver pairs, hand terminated to RJ45. Note that no Cat certification is published for it, which is worth stating plainly rather than assuming. Ethernet is packetised, checksummed and retransmitted on error, and a streamer buffers seconds of audio, so there is no analogue path between the ends. The engine applies no signal processing here.',
  }),

  // =========================================================================
  // Cullen Cable -- United States. Hand-built in-house, and one of the few
  // makers at any price that puts gauge, dielectric and shield coverage on the
  // product page.
  // =========================================================================
  makeCable({
    mfr: 'Cullen Cable',
    model: 'Crossover Series Power Cable',
    cableType: 'power',
    price: usd(209, 'CURRENT_RETAIL', 2025, 'USD 209 for a 6 ft (1.83 m) cord; USD 20 per additional foot.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 12,
    currentRatingA: 20,
    dielectric: 'Teflon (PTFE)',
    shielding: 'Tinned copper braid, 100 percent coverage',
    termination: ['NEMA 5-15', 'NEMA 5-20', 'IEC C13', 'IEC C19'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a24c',
    tags: ['published-construction', 'shielded', 'cryo-treated'],
    provenanceNote:
      'Construction as published by Cullen Cable: three 12 AWG silver plated mil-spec conductors braided together, Teflon dielectric, 100 percent tinned copper braid shield, Techflex jacket, connectors 18k platinum over OFC copper and cryo treated 48 hours at -192 C, 15 A or 20 A terminations with a Hubbell 320HC for the 20 A IEC. No electrical figures published.',
    notes:
      'Line, neutral and earth braided together, which cancels much of the external field the same way a braided speaker cable does, under a full tinned copper braid screen. 12 AWG at 20 A is a properly specified mains lead. The engine applies NO signal processing to power cords, so what this entry contributes to the chain is its description and its price.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Crossover Series II C19 Power Cord',
    cableType: 'power',
    price: usd(279, 'APPROXIMATE', 2025, 'Approximate; Cullen lists the C19 version separately from the 6 ft C13 cord at USD 209.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 12,
    currentRatingA: 20,
    dielectric: 'Teflon (PTFE)',
    shielding: 'Tinned copper braid, 100 percent coverage',
    termination: ['NEMA 5-20', 'IEC C19'],
    confidence: 'ESTIMATED',
    accent: '#d4a24c',
    tags: ['shielded', 'high-current'],
    provenanceNote:
      'Cullen Cable lists the Crossover Series II C19 cord in its catalogue; the construction recorded here is that of the Crossover Series cord, which Cullen publishes in full, and the price is an approximation rather than a quoted figure.',
    notes:
      'The 20 A C19 version of the braided Crossover cord, for power amplifiers and conditioners with a C20 inlet. Same conductor set, heavier connector standard. No signal processing is applied to power cords.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Gold Series Power Cable',
    cableType: 'power',
    price: usd(149, 'CURRENT_RETAIL', 2025, 'USD 149 for a 6 ft (1.83 m) cord; USD 15 per additional foot.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 30,
    dielectric: 'Polyethylene',
    termination: ['NEMA 5-15', 'NEMA 5-20', 'IEC C13', 'IEC C19'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a24c',
    tags: ['published-construction', 'high-current', 'cryo-treated'],
    provenanceNote:
      'Construction as published by Cullen Cable: three 10 AWG 99.99 percent OFC conductors, each of 41 strands of 30 AWG, spun in a rotation, polyethylene dielectric, Techflex jacket, 24k gold over OFC copper connectors cryo treated at -192 C, 30 A rated with 15 A or 20 A terminations.',
    notes:
      'Three 10 AWG OFC conductors of 41 fine strands each, laid with a rotation, rated at 30 A. This is heavier cable than most domestic mains wiring, which is the substantive engineering point. It also sits ahead of a rectifier and a reservoir bank, and the engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Red Copper Series Power Cable',
    cableType: 'power',
    price: usd(99, 'APPROXIMATE', 2025, 'Approximate price for a 6 ft (1.83 m) cord.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['NEMA 5-15', 'IEC C13'],
    confidence: 'ESTIMATED',
    accent: '#d4a24c',
    tags: ['entry-line'],
    provenanceNote:
      'The Red Copper Series cord appears in the Cullen Cable catalogue but the product page reviewed did not state gauge, dielectric or price. 12 AWG and a 15 A rating are assumed from the position of the cable in the range; all electrical figures derived.',
    notes:
      'The entry mains cord in the Cullen line, in plain OFC copper. Recorded here with an explicit note that its gauge is assumed rather than published. No signal processing is applied to power cords.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Crossover Series Speaker Cables',
    cableType: 'speaker',
    price: usd(310, 'CURRENT_RETAIL', 2025, 'USD 310 for a 6 ft (1.83 m) pair; USD 350 for the bi-wire version.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a24c',
    tags: ['published-construction', 'bi-wire-option'],
    provenanceNote:
      'Construction as published by Cullen Cable: 12 AWG silver plated copper mil-spec conductors, Teflon wrap, Techflex sleeve, a ferrite sleeve fitted over each conductor, gold plated solid copper spades or 9-leaf bananas, single-wire or bi-wire. No electrical figures published.',
    notes:
      'Silver plated 12 AWG in Teflon, with a ferrite sleeve over each conductor. The ferrite adds series impedance in the megahertz region and is essentially invisible in the audio band, so what the engine sees is a 12 AWG loop: a few tens of milliohms over a short run, which shows up as a small level and damping change into a low-impedance load.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Copper Speaker Cables',
    cableType: 'speaker',
    price: usd(159, 'CURRENT_RETAIL', 2025, 'USD 159 for a 6 ft (1.83 m) pair; USD 199 for the bi-wire version.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a24c',
    tags: ['published-construction', 'bi-wire-option'],
    provenanceNote:
      'Construction as published by Cullen Cable: high purity oxygen-free 12 AWG conductors with polyethylene dielectric, multi-twist rope lay braiding, 9 mm PVC jacket under Techflex, gold plated spade or banana terminations, single-wire or bi-wire.',
    notes:
      'Rope-lay braided 12 AWG OFC. The braid means adjacent strands carry opposing current over part of their length, which lowers loop inductance relative to a simple parallel pair and raises capacitance in the same proportion. Into a loudspeaker the capacitance is harmless; the inductance is the half that matters at the top of the band.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Copper Series RCA Interconnects',
    cableType: 'interconnect',
    price: usd(119, 'CURRENT_RETAIL', 2025, 'USD 119 for a 1 m pair; USD 109 at 0.5 m, USD 159 at 3 m.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 18,
    dielectric: 'Teflon (PTFE)',
    shielding: 'OFC copper braid, 98 percent coverage, with drain wire',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a24c',
    tags: ['published-construction', 'shielded'],
    provenanceNote:
      'Construction as published by Cullen Cable: 18 AWG 99.99 percent OFC conductors, Teflon dielectric, 98 percent OFC braid shield with drain wire, 8 mm PVC jacket under Techflex, locking gold plated copper connectors with carbon fibre shells. No electrical figures published.',
    notes:
      'An 18 AWG centre conductor is far heavier than a line-level cable needs, which costs nothing electrically and makes the assembly robust. Screened coaxial construction of this kind runs near 85 pF per metre, so a 1 m run against a source of a few hundred ohms puts the corner frequency well into the megahertz.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Copper Series XLR Interconnects',
    cableType: 'interconnect',
    price: usd(159, 'APPROXIMATE', 2025, 'Approximate price for a 1 m balanced pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 20,
    dielectric: 'Teflon (PTFE)',
    shielding: 'OFC copper braid with drain wire',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d4a24c',
    tags: ['balanced', 'shielded'],
    provenanceNote:
      'Cullen Cable lists a Copper Series XLR interconnect but the page reviewed gives detail only for the RCA version. Conductor material and dielectric are taken from that published RCA specification; gauge and price are assumptions.',
    notes:
      'The balanced version of the Copper Series lead: a screened twisted pair with the drain landed at the connector. Twisting sets the loop area and therefore the inductance, and the shield carries no signal current in a properly wired balanced link.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Crossover Series RCA Interconnects',
    cableType: 'interconnect',
    price: usd(229, 'APPROXIMATE', 2025, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 20,
    dielectric: 'Teflon (PTFE)',
    shielding: 'Braided copper screen with drain wire',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#d4a24c',
    tags: ['shielded'],
    provenanceNote:
      'Cullen Cable lists Crossover Series RCA interconnects; the silver plated conductor and Teflon dielectric follow the published Crossover cable stock, while gauge and price are assumptions rather than quoted figures.',
    notes:
      'The Crossover conductor stock, silver plated copper in Teflon, assembled as a single-ended interconnect. Silver plating affects only the outer skin of the conductor, so the DC loop resistance is that of the copper core; at audio frequencies skin depth is far larger than the plating, which is why the kit treats the DC path as copper.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'AES/EBU 110 Ohm Digital Cable',
    cableType: 'interconnect',
    price: usd(199, 'APPROXIMATE', 2025, 'Approximate price for a 1 m cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    characteristicOhms: 110,
    digitalStandard: 'AES3 (AES/EBU), 110 ohm',
    shielding: 'Braided copper screen',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d4a24c',
    tags: ['digital', 'balanced', 'impedance-controlled'],
    provenanceNote:
      'Cullen Cable lists this cable by its impedance, which is the AES3 requirement. Gauge, shielding and price are assumptions; no electrical figures are published.',
    notes:
      'A 110 ohm screened pair terminated in XLR, which is what AES3 calls for. Filed as an interconnect because the schema has no digital category; the analogue capacitance calculation the engine runs on interconnects does not describe a self-clocked digital link.',
  }),

  makeCable({
    mfr: 'Cullen Cable',
    model: 'Nitin Digital Cable',
    cableType: 'interconnect',
    price: usd(179, 'APPROXIMATE', 2025, 'Approximate price for a 1 m cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    characteristicOhms: 75,
    digitalStandard: 'S/PDIF, 75 ohm coaxial',
    shielding: 'Braided copper screen',
    termination: ['RCA', 'BNC'],
    confidence: 'ESTIMATED',
    accent: '#d4a24c',
    tags: ['digital', 'impedance-controlled'],
    provenanceNote:
      'The Nitin digital cable is listed in the Cullen Cable catalogue; the 75 ohm figure is the S/PDIF requirement rather than a published measurement, and gauge, shielding and price are assumptions.',
    notes:
      'A 75 ohm coaxial S/PDIF lead. Impedance continuity from source to load is the property that matters for a digital coaxial link, since reflections from an impedance step are what turn into jitter at the receiver; that is a transmission-line question rather than the lumped R, L and C the engine models.',
  }),

  // =========================================================================
  // Grimm Audio -- Netherlands. A deliberately small cable range: two families,
  // both about symmetry, shielding and microphonics. No speaker or mains cable
  // is made, so none is listed.
  // =========================================================================
  makeCable({
    mfr: 'Grimm Audio',
    model: 'SQM XLR',
    cableType: 'interconnect',
    price: usd(520, 'APPROXIMATE', 2024, 'EUR 475 for a 1 m pair with Neutrik XLRs; converted. Sold as a matched stereo pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 24,
    shielding: 'Tightly woven braided screen over carbon-doped conductor sheaths',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a7d9a',
    tags: ['balanced', 'published-construction', 'low-microphony'],
    provenanceNote:
      'Construction as published by Grimm Audio: star quad conductor layout, carbon-doped sheaths around the individual conductors and beneath the braided screen, tightly woven screen, Neutrik gold plated XLRs, designed and made in the Netherlands. Gauge is not published and is assumed from the small overall diameter. Grimm publishes no R, L or C figures, so all three are derived.',
    notes:
      'Star quad puts diagonally opposite conductors in each leg, so an external magnetic field induces equal voltages in both legs and the difference amplifier rejects them. The carbon-doped sheath is a semiconducting layer that drains the static charge flexing generates between conductor and dielectric, which is the actual mechanism behind cable microphony rather than a metaphor for one.',
  }),

  makeCable({
    mfr: 'Grimm Audio',
    model: 'SQM RCA',
    cableType: 'interconnect',
    price: usd(560, 'APPROXIMATE', 2024, 'Approximately EUR 510 for a 1 m pair with WBT NextGen RCAs; converted.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 24,
    shielding: 'Tightly woven braided screen over carbon-doped conductor sheaths',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a7d9a',
    tags: ['published-construction', 'low-microphony'],
    provenanceNote:
      'Same cable stock as the SQM XLR, terminated with WBT NextGen RCAs as published by Grimm Audio. Gauge assumed; price approximated from the XLR version, since the RCA assembly is listed at a small premium. No electrical figures published.',
    notes:
      'The same star quad stock wired for unbalanced use, with the quad paralleled two and two so the cable keeps its symmetry even without a differential receiver. WBT NextGen connectors use a minimal amount of conductor metal in the shell, which limits eddy current paths at the termination.',
  }),

  makeCable({
    mfr: 'Grimm Audio',
    model: 'TPR',
    cableType: 'interconnect',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR assembly; also sold as bulk cable by the metre.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Heavy braided screen',
    termination: ['XLR', 'RCA', 'bare'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a7d9a',
    tags: ['balanced', 'studio', 'low-microphony'],
    provenanceNote:
      'Grimm Audio publishes the design intent for TPR -- Twisted Pair Reference, symmetric cross-section, heavy shielding, low microphonics, line or mic level, sold in bulk or as assemblies -- but no gauge and no electrical figures. Gauge assumed; R, L and C derived.',
    notes:
      'The studio cable the range is built on, developed for a large Dutch studio rebuild and sold both by the metre and as finished leads. A tight twist and a symmetric cross-section are what give a balanced pair its common-mode rejection; the heavy screen handles the electric field, the twist handles the magnetic one.',
  }),

  makeCable({
    mfr: 'Grimm Audio',
    model: 'TPR AES/EBU',
    cableType: 'interconnect',
    price: usd(130, 'APPROXIMATE', 2024, 'Approximate price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 110,
    digitalStandard: 'AES3 (AES/EBU), 110 ohm',
    shielding: 'Heavy braided screen',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#5a7d9a',
    tags: ['digital', 'balanced', 'impedance-controlled'],
    provenanceNote:
      'Sold by Grimm dealers as a 110 ohm AES/EBU assembly of the TPR cable. The 110 ohm value is the AES3 requirement; Grimm does not publish a measured impedance, and gauge and price are assumptions.',
    notes:
      'TPR assembled as a 110 ohm AES3 lead. Same twisted, screened pair as the analogue version, terminated and specified for a digital link, so the interconnect maths the engine performs is not the right description of its job.',
  }),

  makeCable({
    mfr: 'Grimm Audio',
    model: 'TPR8',
    cableType: 'interconnect',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate price per metre of eight-channel bulk cable.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Individually screened pairs',
    termination: ['sub-D', 'XLR', 'bare'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a7d9a',
    tags: ['multichannel', 'studio'],
    provenanceNote:
      'Grimm Audio describes TPR8 as eight channels of TPR suited to sub-D break-out use. Gauge and price are assumptions; no electrical figures are published.',
    notes:
      'Eight TPR pairs in one jacket for sub-D break-out looms. Per channel the electrical picture is the same as a single TPR; bundling changes crosstalk and handling, not the series impedance of any one pair. There is a sixteen-channel TPR16 built the same way with a slightly reduced diameter.',
  }),

  makeCable({
    mfr: 'Grimm Audio',
    model: 'TPM',
    cableType: 'interconnect',
    price: usd(300, 'DISCONTINUED_USED', 2020, 'Discontinued; approximate secondhand price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Heavy braided screen',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#5a7d9a',
    tags: ['discontinued', 'studio'],
    provenanceNote:
      'TPM -- Twisted Pair Monitoring -- was the Grimm flagship before SQM replaced it, per the Grimm product history. Gauge, shield detail and price are assumptions; no electrical figures were ever published.',
    notes:
      'The twisted pair flagship that SQM superseded when Grimm moved the top of the range to star quad. Same design priorities: symmetric cross-section, heavy screen, low microphony.',
  }),

  // =========================================================================
  // Ricable -- Italy. Made in Oleggio, four tiers, and a broad catalogue that
  // genuinely includes USB.
  // =========================================================================
  makeCable({
    mfr: 'Ricable',
    model: 'Primus Speaker MKII',
    cableType: 'speaker',
    price: usd(232, 'CURRENT_RETAIL', 2025, 'From USD 232 for a 2 x 1.5 m pair at a US dealer.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['entry-line'],
    provenanceNote:
      'Ricable describes its conductor stock as 7N copper produced by its MARC process and does not publish a crystal-structure specification per tier, so the entry lines are recorded as OFC here. Gauge is inferred from the stated conductor section; no electrical figures are published.',
    notes:
      'The entry speaker cable, handmade in Italy with a lifetime warranty. Gauge and run length are what set the loop resistance and therefore the only modelled effect: over a 1.5 m run at this section, tens of milliohms in series with the loudspeaker.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Magnus Speaker MKII',
    cableType: 'speaker',
    price: usd(371, 'CURRENT_RETAIL', 2025, 'From USD 371 at a US dealer; from EUR 305 for a 2 m pair direct.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['mid-line'],
    provenanceNote:
      'Ricable states 7N MARC copper and a double R-TEC dielectric for the Magnus line but no gauge or electrical data; gauge is inferred from the stated conductor section. Figures derived.',
    notes:
      'A heavier conductor section than the Primus with the double R-TEC dielectric Ricable specifies for this line. Doubling the copper section roughly halves the loop resistance, which is the one change in a speaker cable that reliably shows up in a measurement into a low-impedance load.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Dedalus Speaker MKII',
    cableType: 'speaker',
    price: usd(560, 'APPROXIMATE', 2025, 'Approximately USD 560 for a 2 x 1.5 m pair.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['high-line'],
    provenanceNote:
      'Ricable describes the Dedalus line as derived from the Invictus design and its conductors as monocrystalline 7N copper, which is recorded here as OCC. Gauge inferred from the stated section; price approximate; no electrical figures published.',
    notes:
      'The upper value tier, using the same conductor process as the flagship in a lighter dress. At around 10 AWG the loop resistance over a normal domestic run is small enough that the amplifier damping factor barely moves.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Invictus Speaker Reference',
    cableType: 'speaker',
    price: usd(1290, 'APPROXIMATE', 2024, 'From EUR 1,190 for a 2 m pair; converted.'),
    priceLengthM: 2,
    country: 'Italy',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 9,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['flagship'],
    provenanceNote:
      'Ricable publishes the MARC 7N monocrystalline copper conductor and the MKII revision but not gauge or electrical figures; gauge is inferred from the stated conductor section. Price converted from the EUR list.',
    notes:
      'The flagship speaker cable, with the heaviest conductor section in the range. Beyond a certain section the returns are arithmetic: halving an already small loop resistance halves an already small series loss. The engine will show exactly how small, given the load impedance and run length you choose.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Primus Power',
    cableType: 'power',
    price: usd(179, 'CURRENT_RETAIL', 2025, 'From USD 179 for a 1.5 m cord at a US dealer.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['entry-line'],
    provenanceNote:
      'Ricable publishes conductor material and connector types for the Primus cord but not gauge or current rating; both are assumed here from the stated section and the Schuko standard.',
    notes:
      'The entry mains cord, handmade in Italy with gold plated connectors. The engine applies no signal processing to power cords, so this entry is a description of a cable and a price, and nothing else.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Magnus Power MKII',
    cableType: 'power',
    price: usd(339, 'CURRENT_RETAIL', 2025, 'From USD 339 at a US dealer; from EUR 297 for a 1.5 m cord direct.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Aluminium and mylar foil; a second foil shield was added for the MKII',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['shielded'],
    provenanceNote:
      'Ricable states that the MKII revision of the Magnus cord adds a second aluminium and mylar shield. Gauge and current rating are not published and are assumed.',
    notes:
      'A double foil-shielded mains cord. A foil screen with a drain is a proper defence against radiated pickup on a mains lead and costs nothing electrically. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Dedalus Power Elite',
    cableType: 'power',
    price: usd(743, 'CURRENT_RETAIL', 2025, 'From USD 743 for a 1.5 m cord at a US dealer.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Multiple foil shields',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['shielded', 'high-line'],
    provenanceNote:
      'Ricable lists Dedalus Power and Dedalus Power Elite separately and describes 7N monocrystalline copper for the line; gauge, shielding detail and current rating are assumptions.',
    notes:
      'The Elite version of the Dedalus cord, with a heavier conductor section than the standard Dedalus Power. Sound engineering applied to a part of the chain where the engine applies zero DSP, which is the honest description of every mains cord in this database.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Invictus Power',
    cableType: 'power',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximately EUR 1,000 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Multiple foil shields',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['flagship', 'shielded', 'high-current'],
    provenanceNote:
      'Ricable lists a power cord in the Invictus range and publishes MARC 7N monocrystalline copper for that range. Gauge, shielding, rating and price are approximations rather than quoted figures.',
    notes:
      'The flagship mains cord: heaviest section in the range, multiple screens, IEC C19 available for high-current inlets. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Primus Signal RCA',
    cableType: 'interconnect',
    price: usd(96, 'APPROXIMATE', 2025, 'From USD 96 at a US dealer for the shortest pair.'),
    priceLengthM: 1,
    country: 'Italy',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided screen',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['entry-line', 'shielded'],
    provenanceNote:
      'Ricable publishes conductor material and connector type but not gauge, capacitance or shield coverage; those are assumed. The dealer price is a starting figure whose length is not stated, so 1 m is used.',
    notes:
      'The entry single-ended interconnect. A screened coaxial line-level cable of this section runs near 85 pF per metre, which against any normal source impedance puts the resulting low-pass corner far above the audio band; the engine reports the in-band figure it computes.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Magnus XLR',
    cableType: 'interconnect',
    price: usd(246, 'CURRENT_RETAIL', 2025, 'From USD 246 for a balanced pair at a US dealer.'),
    priceLengthM: 1,
    country: 'Italy',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil and braid',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['balanced', 'shielded'],
    provenanceNote:
      'Conductor material and the double R-TEC dielectric are published for the Magnus line; gauge and shield construction are assumptions. No electrical figures published.',
    notes:
      'A screened twisted pair for balanced links. Balanced wiring rejects what the two legs pick up in common, so the cable specification that matters is the symmetry of the pair rather than the metal in it.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Invictus XLR',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximately EUR 640 for a 1 m balanced pair; converted.'),
    priceLengthM: 1,
    country: 'Italy',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Foil and braid',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['balanced', 'flagship'],
    provenanceNote:
      'Ricable publishes MARC 7N monocrystalline copper for the Invictus range; gauge, shield construction and price are approximations rather than quoted figures.',
    notes:
      'The flagship balanced interconnect, in the same monocrystalline copper as the rest of the Invictus range. Whatever the conductor, a metre of well-made screened pair between a line output and a line input is close to electrically invisible in the audio band, and the engine will say so.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Magnus USB',
    cableType: 'usb',
    price: usd(107, 'CURRENT_RETAIL', 2025, 'From USD 107 at a US dealer.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#d64545',
    tags: ['usb-2.0'],
    provenanceNote:
      'Ricable publishes the USB 2.0 A to B format and conductor material; gauge, shield construction and the 90 ohm figure (which is the USB requirement) are assumed.',
    notes:
      'A USB 2.0 A to B assembly with the data pair screened separately from the bus power conductors. USB audio is packetised and error-checked and the DAC clock is generated at the DAC, so a compliant cable transfers the data or fails. The engine applies no signal processing here.',
  }),

  makeCable({
    mfr: 'Ricable',
    model: 'Invictus USB',
    cableType: 'usb',
    price: usd(467, 'CURRENT_RETAIL', 2025, 'USD 467 for a 1.5 m cable; made to order from 0.5 m to 5 m.'),
    priceLengthM: 1.5,
    country: 'Italy',
    conductor: 'occ-copper',
    geometry: 'concentric',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid, data pair screened from the power conductors',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['usb-2.0', 'flagship'],
    provenanceNote:
      'Construction as published by Ricable: MARC 7N monocrystalline copper, USB 2.0 type A to type B, an asymmetric geometry that keeps the data conductors concentric and the power conductors parallel, assembled in Oleggio with a lifetime warranty. Gauge and the 90 ohm figure are the USB norms rather than published values.',
    notes:
      'The interesting part of this build is the geometry: the data pair is kept concentric while the 5 V and ground conductors run parallel alongside, which is a sensible way to keep bus power out of the differential pair. That is engineering worth recording. It is also a packetised, error-checked link, and the engine applies no signal processing to USB.',
  }),

  // =========================================================================
  // XLO -- United States. Field-Balanced winding geometry across four tiers,
  // PC-OCC and 6N copper, purple Teflon jackets.
  // =========================================================================
  makeCable({
    mfr: 'XLO',
    model: 'UltraPLUS UP1A',
    cableType: 'interconnect',
    price: usd(200, 'APPROXIMATE', 2024, 'Approximately USD 200 for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'solid-core',
    gauge: 22,
    dielectric: 'Teflon (FEP)',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['solid-conductor', 'published-construction'],
    provenanceNote:
      'XLO publishes single-filament solid core PC-OCC conductors, a Teflon jacket, the Field-Balanced surface and diving winding geometry, and hand-terminated 24k gold plated RCAs. It claims low capacitance and inductance but publishes no figures, so R, L and C are derived. Gauge assumed.',
    notes:
      'Single-filament solid core PC-OCC in Teflon. XLO winds the conductors so that each one alternates between the surface and the centre of the bundle, which equalises the path length and the field each sees rather than leaving one conductor permanently on the outside. Solid, spaced conductors put this at the low-capacitance end of the trade-off.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'UltraPLUS UP4A',
    cableType: 'interconnect',
    price: usd(150, 'APPROXIMATE', 2024, 'Approximately USD 150 for a 2 m cable.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    characteristicOhms: 75,
    digitalStandard: 'S/PDIF, 75 ohm coaxial',
    dielectric: 'Teflon (FEP)',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#7b5ea7',
    tags: ['digital', 'impedance-controlled'],
    provenanceNote:
      'UP4A is the coaxial digital cable in the UltraPLUS range; the PC-OCC conductor and Teflon dielectric follow the published UltraPLUS stock, while the 75 ohm value is the S/PDIF requirement and gauge and price are assumptions.',
    notes:
      'A 75 ohm coaxial S/PDIF lead built from the UltraPLUS conductor stock. Filed as an interconnect for want of a nearer category; what matters on a digital coaxial run is impedance continuity end to end, not the lumped capacitance the engine computes for analogue links.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'UltraPLUS Speaker Cable',
    cableType: 'speaker',
    price: usd(176, 'CURRENT_RETAIL', 2025, 'USD 176.25 for an 8 ft (2.4 m) banana-terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Teflon (FEP)',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['published-construction'],
    provenanceNote:
      'XLO publishes PC-OCC conductors, the Teflon jacket and the Field-Balanced surface and diving winding geometry for the UltraPLUS range, which is mapped to braided norms in this kit. Gauge is not published and is assumed.',
    notes:
      'The Field-Balanced winding interleaves the conductors so no single run stays on the outside of the bundle. Any interleaved geometry brings adjacent opposing currents closer together, which lowers loop inductance and raises capacitance in the same proportion. Into a loudspeaker that is the right side of the trade to be on.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'UltraPLUS AC Power Cable',
    cableType: 'power',
    price: usd(142, 'CURRENT_RETAIL', 2025, 'USD 142.50 for a 6 ft (1.83 m) terminated cord; USD 134.95 at 3 ft.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['NEMA 5-15', 'IEC C13'],
    confidence: 'ESTIMATED',
    accent: '#7b5ea7',
    tags: ['entry-line'],
    provenanceNote:
      'XLO sells the UltraPLUS AC cord in terminated 3 ft and 6 ft lengths at published dealer prices; conductor material, gauge and rating are not published and are assumed from the class of cable.',
    notes:
      'A terminated mains cord in the UltraPLUS dress, sold at two fixed lengths. The engine applies no signal processing to power cords: what this entry contributes to a chain is its description and its price.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Reference 3 Interconnect',
    cableType: 'interconnect',
    price: usd(217, 'CURRENT_RETAIL', 2025, 'USD 216.95 for a 1 m RCA pair; USD 317.25 at 2 m.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 22,
    dielectric: 'Teflon (FEP)',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['published-construction'],
    provenanceNote:
      'XLO publishes 6N laboratory-grade copper conductors in a quasi-random Field-Balanced surface and diving wind for Reference 3. That is not an Ohno-process conductor, so it is recorded as OFC. Gauge assumed; no electrical figures published.',
    notes:
      'Reference 3 uses 6N copper in the quasi-random Field-Balanced wind, one tier below the Ohno-process conductor of the Signature line. The winding is the substantive difference between XLO tiers; the resistivity difference between 6N and UP-OCC copper of the same section is under one percent.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Reference 3 XLR Interconnect',
    cableType: 'interconnect',
    price: usd(245, 'CURRENT_RETAIL', 2025, 'USD 244.95 for a 1 m balanced pair; USD 294.75 at 1.5 m.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 22,
    dielectric: 'Teflon (FEP)',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['balanced', 'published-construction'],
    provenanceNote:
      'Same published Reference 3 construction as the RCA version, terminated in XLR. Gauge assumed; no electrical figures published.',
    notes:
      'The balanced Reference 3 lead. In a balanced link the receiver subtracts the two legs, so interference common to both cancels; the cable job is to keep the two legs identical, which is what a symmetric interleaved wind is for.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Reference 3 Speaker Cable',
    cableType: 'speaker',
    price: usd(793, 'CURRENT_RETAIL', 2025, 'USD 792.95 for an 8 ft (2.4 m) banana-terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Teflon (FEP)',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['published-construction'],
    provenanceNote:
      'XLO publishes 6N copper and the Field-Balanced wind for Reference 3; gauge is not published and is assumed. Electrical figures derived from gauge, material and geometry.',
    notes:
      'The mid-tier XLO speaker cable, in 6N copper with the interleaved wind. Series resistance from the gauge and series inductance from the geometry are the two properties that put a speaker cable into the amplifier-to-load divider; both are derived here rather than asserted.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Reference 3 AC Power Cable',
    cableType: 'power',
    price: usd(266, 'CURRENT_RETAIL', 2025, 'USD 265.50 for a 6 ft (1.83 m) terminated cord.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['NEMA 5-15', 'IEC C13'],
    confidence: 'ESTIMATED',
    accent: '#7b5ea7',
    tags: ['mid-line'],
    provenanceNote:
      'The 6 ft terminated Reference 3 cord is sold at a published dealer price; XLO does not publish its gauge, conductor detail or current rating, so those are assumed.',
    notes:
      'The Reference tier mains cord. Recorded with the same honesty as every other cord in this file: the engine applies no signal processing to power cords, and the audio output is bit-identical whichever one is selected.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Signature 3 Interconnect',
    cableType: 'interconnect',
    price: usd(394, 'CURRENT_RETAIL', 2025, 'USD 393.75 for a 1 m RCA pair; USD 592.50 at 2 m.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 21,
    dielectric: 'Teflon (FEP)',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['published-construction', 'flagship'],
    provenanceNote:
      'XLO publishes UP-OCC 6N copper -- Ohno continuous cast -- and the Field-Balanced wind for Signature 3. Gauge assumed; no electrical figures published, despite a general XLO claim of low capacitance and inductance.',
    notes:
      'The top analogue interconnect: Ohno continuous cast 6N copper, which draws the conductor so grain boundaries run along rather than across it, in the same interleaved wind as the lower tiers. Grain structure has no measurable effect on resistivity at audio frequencies; the geometry and the section are what the engine can act on.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Signature 3 XLR Interconnect',
    cableType: 'interconnect',
    price: usd(424, 'CURRENT_RETAIL', 2025, 'USD 423.75 for a 1 m balanced pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 21,
    dielectric: 'Teflon (FEP)',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['balanced', 'published-construction'],
    provenanceNote:
      'Same published Signature 3 construction as the RCA version, terminated in XLR. Gauge assumed; no electrical figures published.',
    notes:
      'The balanced Signature 3 lead, in UP-OCC 6N copper with the Field-Balanced wind. Capacitance against the source impedance is the only in-band mechanism there is here, and on a metre of cable it works out to a fraction of a decibel at the very top of the band at worst.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Signature 3 Speaker Cable',
    cableType: 'speaker',
    price: usd(3700, 'MSRP', 2010, 'USD 3,700 for an 8 ft (2.4 m) bi-wire pair at the time of the review.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 11,
    dielectric: 'Teflon (FEP)',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7b5ea7',
    tags: ['published-construction', 'bi-wire-option', 'flagship'],
    provenanceNote:
      'XLO publishes UP-OCC 6N copper and the Field-Balanced surface and diving wind for Signature 3. Gauge is not published and is assumed from the cable diameter; the price is the quoted bi-wire figure from a published review rather than a current dealer price.',
    notes:
      'The flagship speaker cable, offered single-wire or bi-wire. Bi-wiring splits the run into two loops, one per driver section, which halves the current in each conductor set and separates the return paths of the bass and treble sections; that is a real topological change, and the engine models each run as the cable it is.',
  }),

  makeCable({
    mfr: 'XLO',
    model: 'Signature 3 AC Power Cable',
    cableType: 'power',
    price: usd(516, 'CURRENT_RETAIL', 2025, 'USD 515.63 for a 6 ft (1.83 m) terminated cord.'),
    priceLengthM: 1.83,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['NEMA 5-15', 'IEC C13'],
    confidence: 'ESTIMATED',
    accent: '#7b5ea7',
    tags: ['flagship'],
    provenanceNote:
      'The 6 ft terminated Signature 3 cord is sold at a published dealer price; XLO does not publish gauge, conductor detail or current rating for it, so those are assumed from the tier.',
    notes:
      'The top mains cord in the XLO range. Every part of this entry is a construction and commercial fact; the engine applies no signal processing to power cords, and nothing in the audio path changes when it is selected.',
  }),

  // =========================================================================
  // Monster Cable -- United States. The company that created the category in
  // 1978. Most of the hi-fi range is long discontinued; prices reflect that.
  // =========================================================================
  makeCable({
    mfr: 'Monster Cable',
    model: 'Original Monster Cable',
    cableType: 'speaker',
    price: usd(90, 'APPROXIMATE', 2024, 'About USD 90 for a 3 m pair cut and terminated from bulk.'),
    priceLengthM: 3,
    year: 1979,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['historic', 'bulk-cable'],
    provenanceNote:
      'The founding Monster product, described in period material as roughly 12 AWG stranded copper in a heavy parallel-conductor jacket. Monster published no electrical figures for it; gauge is from period descriptions rather than a datasheet.',
    notes:
      'The cable that started the aftermarket speaker cable trade in 1978, and the reason the rest of this directory exists. Two heavy stranded conductors in a bonded parallel jacket. Against the 18 AWG bell wire it replaced, 12 AWG genuinely does cut loop resistance by about a factor of four, which on a long run into a low-impedance load is a real, measurable difference in level and damping.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'XP Clear Jacket',
    cableType: 'speaker',
    price: usd(40, 'CURRENT_RETAIL', 2025, 'About USD 40 for a 15 m (50 ft) spool.'),
    priceLengthM: 15,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 16,
    dielectric: 'LPE (low-density polyethylene)',
    termination: ['bare', 'banana', 'pin'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2fa84f',
    tags: ['bulk-cable', 'entry-line'],
    provenanceNote:
      'Monster publishes 16 AWG two-conductor construction with LPE insulation and a clear jacket for XP. Note that current retail spools are also sold in a copper-clad aluminium version; the OFC version is modelled here. No electrical figures published.',
    notes:
      'The volume product: 16 AWG two-conductor bonded pair in a clear jacket, sold by the spool for in-wall and surround use. 16 AWG is fine for a short run into an 8 ohm speaker and marginal on a long run into 4 ohms, which is exactly the calculation the engine performs from the gauge and the length you pick. A copper-clad aluminium version also exists and has about 54 percent higher resistivity for the same gauge.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'M Series M1.4s',
    cableType: 'speaker',
    price: usd(200, 'DISCONTINUED_USED', 2024, 'Secondhand price for a 2.4 m pair; long discontinued.'),
    priceLengthM: 2.4,
    year: 1992,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued', 'multi-gauge'],
    provenanceNote:
      'The M Series arrived in 1992 as the high-end Monster line, built around multiple conductor gauges wound in a bundle. Monster published no gauge table or electrical figures; the 12 AWG equivalent here is an estimate of the total conductor section, and the geometry is mapped to concentric norms.',
    notes:
      'Monster wound conductors of several different diameters together in one bundle, the stated intention being that no single conductor diameter dominates. What that construction actually fixes is the total section and the loop geometry: several parallel conductors of mixed gauge behave as one conductor of their combined area, with the loop inductance set by how tightly the bundle is wound.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'M Series M2.4s',
    cableType: 'speaker',
    price: usd(400, 'DISCONTINUED_USED', 2024, 'Secondhand price for a 2.7 m (9 ft) bi-wire pair.'),
    priceLengthM: 2.7,
    year: 1992,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 10,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued', 'bi-wire-option', 'multi-gauge'],
    provenanceNote:
      'The heavier M Series cable, sold in bi-wire form. Monster published no conductor table or electrical data; gauge is estimated from the cable diameter and the geometry is mapped to concentric norms.',
    notes:
      'The bi-wire M Series cable, with a heavier total conductor section than the M1.4s. Bi-wire form gives each driver section its own loop, so the current in each conductor set is lower and the return paths are separate. The modelled consequence is simply the loop resistance and inductance of each run.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'Z-Series Z2',
    cableType: 'speaker',
    price: usd(120, 'DISCONTINUED_USED', 2024, 'Secondhand price for a 3 m pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued'],
    provenanceNote:
      'The Z-Series overlapped the M Series: owners and dealers have long noted that Z2 and M1.4 are the same cable in different jackets, as are Z4 and M2.2. Monster published no electrical figures; gauge is estimated.',
    notes:
      'Worth recording precisely because of the overlap: the Z-Series and M Series shared cable stock under different jackets and model numbers, so two differently priced entries in the same catalogue could be electrically identical. Where that is the case, this database says so rather than inventing a difference.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'Prolink Classic Speaker Cable',
    cableType: 'speaker',
    price: usd(30, 'CURRENT_RETAIL', 2025, 'From USD 29.99 for a terminated lead.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    termination: ['1/4 inch TS', 'speakON'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['pro-audio', 'current-product'],
    provenanceNote:
      'One of the Prolink stage cables Monster still sells. Gauge and geometry are not published for the current version and are assumed from the class of cable; no electrical figures published.',
    notes:
      'A stage speaker lead rather than a hi-fi one, terminated for backline use. Included because it is a current Monster product with a real construction: a twisted pair carrying amplifier output, where the twist keeps the loop area down over long runs across a stage.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'Interlink 300',
    cableType: 'interconnect',
    price: usd(60, 'DISCONTINUED_USED', 2024, 'Secondhand price for a 1 m pair.'),
    priceLengthM: 1,
    year: 1985,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued', 'shielded'],
    provenanceNote:
      'Interlink was launched in 1980 as the second Monster product line and ran through numbered tiers for two decades. Monster published no gauge or electrical figures for it; both gauge and shield construction are assumed.',
    notes:
      'A mid-tier Interlink from the era when a screened, decently terminated RCA lead was itself the upgrade over moulded free-with-the-box cable. Screened coaxial construction, so the capacitance is at the higher end for a line-level cable and the low-pass corner is still far above the audio band on a 1 m run.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'Interlink 400',
    cableType: 'interconnect',
    price: usd(80, 'DISCONTINUED_USED', 2024, 'Secondhand price for a 1.8 m (6 ft) pair.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued', 'shielded'],
    provenanceNote:
      'A higher Interlink tier, still sold secondhand in 6 ft terminated pairs. No published gauge, shield detail or electrical figures; all assumed or derived.',
    notes:
      'One tier up the Interlink ladder, with a heavier jacket and a larger connector. At 1.8 m the total shunt capacitance is roughly double that of a 1 m lead, which against a low-impedance solid-state output still leaves the corner frequency in the hundreds of kilohertz.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'Interlink Reference A',
    cableType: 'interconnect',
    price: usd(150, 'DISCONTINUED_USED', 2024, 'Secondhand price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Multiple braided screens',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued', 'shielded'],
    provenanceNote:
      'The top of the Interlink range in its day. Monster published no gauge, shield count or electrical figures; the construction recorded here is inferred from period product descriptions.',
    notes:
      'The flagship Interlink, with a heavier conductor and more screening than the numbered tiers. Extra shield layers reduce radiated pickup; they also raise capacitance, because more grounded metal closer to the conductor is exactly what a capacitor is.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'PowerLine 200',
    cableType: 'power',
    price: usd(60, 'DISCONTINUED_USED', 2024, 'Secondhand price for an 8 ft (2.4 m) cord.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    shielding: 'Mylar foil',
    termination: ['NEMA 5-15', 'IEC C13'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued', 'shielded'],
    provenanceNote:
      'Monster sold the PowerLine cords as detachable 8 ft IEC leads through the Monster Power range. Gauge and rating are not published for this tier and are assumed; the foil shield is described in period product copy.',
    notes:
      'The entry detachable IEC cord in the Monster Power range, with a foil screen and gold plated contacts. The engine applies no signal processing to power cords, so this entry is a description of a mains lead and nothing more.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'PowerLine 300',
    cableType: 'power',
    price: usd(100, 'DISCONTINUED_USED', 2024, 'Secondhand price for an 8 ft (2.4 m) cord.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Mylar foil, full coverage',
    termination: ['NEMA 5-15', 'IEC C13'],
    confidence: 'ESTIMATED',
    accent: '#2fa84f',
    tags: ['discontinued', 'shielded'],
    provenanceNote:
      'Period Monster material describes a full mylar foil shield against RF and EM pickup and power conductors more than twice the section of an ordinary cord. Gauge and rating are inferred from that description rather than published.',
    notes:
      'A heavier detachable IEC cord than the PowerLine 200, with a full foil shield. Conductor section and shielding are the honest specification. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Monster Cable',
    model: 'PowerLine 400 Signature Series',
    cableType: 'power',
    price: usd(200, 'DISCONTINUED_USED', 2024, 'Secondhand price for an 8 ft (2.4 m) cord.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'High-density shield over multi-bundled conductors',
    termination: ['NEMA 5-15', 'NEMA 5-20', 'IEC C13'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2fa84f',
    tags: ['discontinued', 'shielded', 'high-current'],
    provenanceNote:
      'Monster published the conductor detail for this cord: 261 strands of OFC in an 11 gauge multi-bundled construction, 15 mm outer diameter, high-density shielding. The current rating is inferred from that section; no electrical figures were published.',
    notes:
      'The top of the PowerLine range: 261 OFC strands making up an 11 gauge conductor set, in a 15 mm jacket with heavy shielding. That is a substantial mains cable by any standard and a genuinely low-impedance path back to the wall. It also feeds a rectifier, and the engine applies no signal processing to power cords, so the audio is bit-identical with it in the chain.',
  }),
];
